import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material"
import { MdAccessTime, MdTrendingUp } from "react-icons/md"

import GestaoVagaCidadeDistancia from "./GestaoVagaCidadeDistancia.jsx"
import {
	aplicarRotuloVagaTexto,
	resolverRotulosGestaoVaga,
} from "./gestaoVagaRotuloUtil.js"

function formatarFusoParaUsuario(fuso) {
	const id = String(fuso ?? "").trim()
	if (!id) return "—"
	const mapa = {
		"America/Sao_Paulo": "Horário de Brasília",
		"America/Manaus": "Horário do Amazonas",
		"America/Cuiaba": "Horário de Cuiabá",
		"America/Fortaleza": "Horário de Fortaleza",
		"America/Recife": "Horário de Recife",
		"America/Belem": "Horário de Belém",
	}
	return mapa[id] ? `${mapa[id]}` : id
}

function textoUrgencia(principal) {
	const faltam = Number(principal?.urgencia?.faltamNoQuadro ?? principal?.quadro?.faltam ?? 0)
	const min = principal?.urgencia?.minutosRestantes ?? principal?.tempo?.minutosRestantesIngresso
	if (faltam > 1) {
		return `Alta prioridade — faltam ${faltam} profissionais no quadro de hoje`
	}
	if (faltam === 1) {
		return "Alta prioridade — falta 1 profissional no quadro de hoje"
	}
	if (min != null && Number(min) <= 60) {
		return `Corra — restam cerca de ${min} min para se cadastrar`
	}
	return "Oportunidade de hoje — cadastre-se enquanto a vaga está aberta"
}

function txtModal(texto, rotulos) {
	return aplicarRotuloVagaTexto(texto, rotulos ?? resolverRotulosGestaoVaga())
}

/**
 * Modal de vaga urgente para ingresso hoje (titular).
 */
export default function GestaoVagaPrioridadeModal({
	aberto,
	principal,
	relogio = null,
	rotulos,
	onIngressar,
	onVerOutras,
	onFechar,
}) {
	if (!principal) return null

	const txt = (texto) => txtModal(texto, rotulos)

	const tempo = principal?.tempo ?? {}
	const rel = relogio ?? {}
	const valores = principal?.valoresHoje ?? {}
	const titulo = String(principal?.titulo ?? "Vaga")
	const descricao = String(principal?.descricao ?? principal?.descricaoResumo ?? "").trim()

	const labelPiso = String(valores.labelPiso || "Piso mínimo")
	const labelExp = String(valores.labelExpectativa || "Expectativa de ganho")
	const piso = String(valores.pisoMinimo ?? "").trim()
	const expectativa = String(
		valores.expectativaGanho || principal?.valorPreferencia || ""
	).trim()

	const horarioHoje =
		String(valores.horario ?? "").trim() ||
		(tempo.horaInicioJornada
			? `Início ${tempo.horaInicioJornada}${principal.horario ? ` · ${principal.horario}` : ""}`
			: String(principal?.horario ?? ""))

	const ateHora = tempo.ingressoAteHora || ""
	const ateCompleto = tempo.ingressoAteAs || principal.ingressoAteAs || ""
	const prazoCurto = ateHora ? `até ${ateHora}` : ateCompleto ? `até ${ateCompleto.split(" ").pop()}` : ""

	const agoraExibir = tempo.agoraFormatado || rel.agoraFormatado || ""
	const fusoExibir = tempo.fusoHorario || rel.fusoHorario || ""

	return (
		<Dialog
			open={aberto}
			onClose={onFechar}
			maxWidth="sm"
			fullWidth
			className="gv-prioridade-modal"
		>
			<DialogTitle className="gv-prioridade-modal__header">
				<Box className="gv-prioridade-modal__header-top">
					<span className="gv-prioridade-modal__titulo">{txt("Vaga urgente hoje")}</span>
					<Chip
						size="small"
						label="Prioridade alta"
						className="gv-prioridade-modal__chip-urgente"
					/>
				</Box>
				<Typography variant="caption" className="gv-prioridade-modal__subtitulo-urgencia">
					{txt(textoUrgencia(principal))}
				</Typography>
			</DialogTitle>

			<DialogContent className="gv-prioridade-modal__content">
				<Typography variant="h6" component="h2" className="gv-prioridade-modal__nome-vaga">
					{titulo}
				</Typography>

				{principal.cidadeEstado || principal.distanciaTexto ? (
					<GestaoVagaCidadeDistancia
						className="gv-prioridade-modal__local"
						cidadeEstado={principal.cidadeEstado}
						distanciaTexto={principal.distanciaTexto}
					/>
				) : null}

				{(piso || expectativa) && (
					<Box className="gv-prioridade-modal__valores" role="group" aria-label={txt("Valores da vaga")}>
						{piso ? (
							<div className="gv-prioridade-modal__valor-card gv-prioridade-modal__valor-card--piso">
								<span className="gv-prioridade-modal__valor-label">{labelPiso}</span>
								<span className="gv-prioridade-modal__valor-numero">{piso}</span>
							</div>
						) : null}
						{expectativa ? (
							<div className="gv-prioridade-modal__valor-card gv-prioridade-modal__valor-card--exp">
								<span className="gv-prioridade-modal__valor-label">{labelExp}</span>
								<span className="gv-prioridade-modal__valor-numero">{expectativa}</span>
							</div>
						) : null}
					</Box>
				)}

				{descricao ? (
					<Box className="gv-prioridade-modal__descricao">
						<Typography variant="subtitle2" className="gv-prioridade-modal__secao-titulo">
							{txt("Sobre a vaga")}
						</Typography>
						<Typography variant="body2" className="gv-prioridade-modal__descricao-texto">
							{descricao}
						</Typography>
					</Box>
				) : null}

				<Box className="gv-prioridade-modal__prazo-banner">
					<MdAccessTime size={22} className="gv-prioridade-modal__prazo-icone" aria-hidden />
					<div>
						{horarioHoje ? (
							<Typography variant="body2" className="gv-prioridade-modal__prazo-linha">
								Jornada de hoje: <strong>{horarioHoje}</strong>
							</Typography>
						) : null}
						{prazoCurto ? (
							<Typography variant="body1" className="gv-prioridade-modal__prazo-destaque">
								Cadastre-se <strong>{prazoCurto}</strong>
							</Typography>
						) : (
							<Typography variant="body1" className="gv-prioridade-modal__prazo-destaque">
								{txt("Cadastre-se hoje enquanto há vaga no quadro")}
							</Typography>
						)}
					</div>
				</Box>

				{agoraExibir || fusoExibir ? (
					<Typography variant="caption" color="text.secondary" className="gv-prioridade-modal__ref-hora">
						{agoraExibir ? <>Hora atual: {agoraExibir}</> : null}
						{agoraExibir && fusoExibir ? " · " : null}
						{fusoExibir ? <>Fuso: {formatarFusoParaUsuario(fusoExibir)}</> : null}
					</Typography>
				) : null}

				<Typography variant="caption" display="block" color="text.secondary" className="gv-prioridade-modal__rodape-info">
					{txt(
						"Você está se candidatando como titular da vaga. Entrar na fila do dia é um passo depois, na tela da fila."
					)}
				</Typography>
			</DialogContent>

			<DialogActions className="gv-prioridade-modal__acoes">
				<Button
					variant="contained"
					fullWidth
					size="large"
					className="gv-prioridade-modal__btn-principal"
					startIcon={<MdTrendingUp size={20} />}
					onClick={() => onIngressar?.(principal)}
				>
					Quero fazer parte hoje
				</Button>
				<Button variant="outlined" fullWidth onClick={onVerOutras}>
					{txt("Ver outras vagas")}
				</Button>
				<Button variant="text" fullWidth onClick={onFechar}>
					Agora não
				</Button>
			</DialogActions>
		</Dialog>
	)
}
